import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDuration, intervalToDuration, isBefore } from 'date-fns';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthContext } from '../AuthContext';

const API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.insightos.co.uk'
    : 'https://api.insightos.co.uk';

const OTP_LENGTH = 6;

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    const API_BASE =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.insightos.co.uk'
        : 'https://api.insightos.co.uk';

    const OTP_LENGTH = 6;

    <path fill="#EA4335" d="M12 10.2v3.9h5.6c-.2 1.3-1.6 3.8-5.6 3.8-3.4 0-6.1-2.8-6.1-6.2S8.6 5.5 12 5.5c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3 14.7 2 12 2 6.5 2 2.1 6.4 2.1 12S6.5 22 12 22c5.7 0 9.5-4 9.5-9.7 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

const MicrosoftLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#F25022" d="M2 2h9v9H2z" />
    <path fill="#7FBA00" d="M13 2h9v9h-9z" />
    <path fill="#00A4EF" d="M2 13h9v9H2z" />
    <path fill="#FFB900" d="M13 13h9v9h-9z" />
  </svg>
);

const AuthenticatorBox = () => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-inner shadow-black/20">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Authenticator app</p>
        <p className="mt-1 text-sm text-slate-200">Use Google Authenticator or Microsoft Authenticator</p>
      </div>
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-3 py-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5">
          <GoogleLogo />
        </span>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5">
          <MicrosoftLogo />
        </span>
      </div>
    </div>
  </div>
);

function getPasswordStrength(password) {
  let score = 0;
  if (!password) return { label: 'Very Weak', color: 'bg-red-400', score: 0 };
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { label: 'Very Weak', color: 'bg-red-400', score };
  if (score === 2) return { label: 'Weak', color: 'bg-orange-400', score };
  if (score === 3) return { label: 'Moderate', color: 'bg-yellow-400', score };
  if (score === 4) return { label: 'Strong', color: 'bg-green-400', score };
  return { label: 'Very Strong', color: 'bg-emerald-500', score };
}

function PasswordReset() {
  const recaptchaRef = useRef();
  const [step, setStep] = useState(1);
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [passError, setPassError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [expiry, setExpiry] = useState(null);
  const [countdown, setCountdown] = useState('');

  const [captchaToken, setCaptchaToken] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);

  const user = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const normalizeCode = (value) => value.replace(/\D/g, '').slice(0, OTP_LENGTH);

  useEffect(() => {
    if (location.state && location.state.expiry) {
      setExpiry(new Date(location.state.expiry));
    }
    if (location.state && location.state.id) {
      setResetUserId(location.state.id);
    }
  }, [location.state]);

  useEffect(() => {
    const resolveResetSession = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/temp-auth`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json();
        if (payload?.id) {
          setResetUserId(payload.id);
        }
      } catch (_error) {
        // Ignore and let backend fallback to cookie token during verification.
      }
    };

    resolveResetSession();
  }, []);

  useEffect(() => {
    if (!expiry) return;
    const interval = setInterval(() => {
      const now = new Date();
      if (isBefore(expiry, now)) {
        window.location.reload();
        navigate('/');
      } else {
        const dur = intervalToDuration({ start: now, end: expiry });
        setCountdown(formatDuration(dur, { format: ['minutes', 'seconds'] }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expiry, navigate]);

  // Step 1: verify authenticator or backup code
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      const payload = { RandomCode: authCode };
      if (resetUserId) {
        payload.id = resetUserId;
      } else if (user?.id) {
        payload.id = user.id;
      }

      const response = await fetch(`${API_BASE}/api/verify-reset-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 428) {
        setAuthError(data?.message || 'Authenticator setup is required before password reset. Please sign in and set up MFA first.');
        return;
      }

      if (response.ok && data?.success) {
        if (data.expiry) {
          setExpiry(new Date(data.expiry));
        }
        setStep(2);
      } else {
        setAuthError(data?.message || 'Invalid or expired authenticator code.');
      }
    } catch (error) {
      setAuthError(error?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: submit new password (checkbox flow)
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPassError('');
    if (!password || !confirm) {
      setPassError('Please fill in both password fields.');
      return;
    }
    if (password !== confirm) {
      setPassError('Passwords do not match.');
      return;
    }
    const passwordStrength = getPasswordStrength(password);
    if (passwordStrength.score <= 1) {
      setPassError('Password is too weak.');
      return;
    }

    if (!captchaToken) {
      setPassError('Please complete the CAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newPassword: password, captchaToken }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data?.success) {
        setSuccessMsg('Password changed successfully. You may now sign in.');
        setStep(3);
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setCaptchaToken(null);
      } else {
        setPassError(data?.message || 'Failed to change password.');
      }
    } catch (error) {
      setPassError(error?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-8 bg-slate-950">
      <style>{`
        @keyframes forgotFloatA {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(36px, -42px); }
        }
        @keyframes forgotFloatB {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(-30px, 36px); }
        }
      `}</style>

      <div className="pointer-events-none absolute -top-28 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-3xl" style={{ animation: 'forgotFloatA 24s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute -bottom-24 -right-20 h-[380px] w-[380px] rounded-full bg-violet-600/20 blur-3xl" style={{ animation: 'forgotFloatB 28s ease-in-out infinite' }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 text-center">
          <img className="mx-auto h-14 w-14 drop-shadow-lg" src="1.png" alt="InsightOS Logo" loading="eager" />
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white">
            {step === 1 && 'Enter Authenticator Code'}
            {step === 2 && 'Set New Password'}
            {step === 3 && 'Success'}
          </h2>
          <p className="mt-2 text-sm text-slate-400">Secure reset with your authenticator app and CAPTCHA.</p>
          {expiry && step !== 3 && (
            <div className="mt-3 rounded-xl border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              Link expires in: {countdown || 'less than a minute'}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-slate-900/75 px-6 py-7 shadow-2xl backdrop-blur-xl sm:px-8">
          {step === 1 && (
            <form className="space-y-4" onSubmit={handleAuthSubmit}>
              <AuthenticatorBox />
              <p className="text-sm leading-6 text-slate-300">
                Enter the 6-digit code from your authenticator app or one of your backup codes.
              </p>
              <div>
                <label htmlFor="authCode" className="mb-2 block text-sm font-medium text-white">Authenticator Or Backup Code</label>
                <input
                  id="authCode"
                  name="authCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={OTP_LENGTH}
                  value={authCode}
                  onChange={(e) => setAuthCode(normalizeCode(e.target.value))}
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-center text-lg font-semibold tracking-[0.3em] text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                  placeholder="123456"
                />
              </div>
              {authError && <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">{authError}</div>}
              <button
                type="submit"
                disabled={loading || authCode.replace(/\D/g, '').length !== OTP_LENGTH}
                className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                  loading || authCode.replace(/\D/g, '').length !== OTP_LENGTH
                    ? 'cursor-not-allowed bg-indigo-500/40'
                    : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                }`}
              >
                {loading ? 'Verifying...' : 'Submit Code'}
              </button>
            </form>
          )}

          {step === 2 && (
            <form className="space-y-4" onSubmit={handlePasswordSubmit}>
              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-medium text-white">New Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                />
                <div className="mt-2 flex items-center">
                  <div className={`h-2 w-2 rounded-full mr-2 ${passwordStrength.color}`} />
                  <span className={`text-xs font-semibold ${passwordStrength.color.replace('bg-', 'text-')}`}>{passwordStrength.label}</span>
                </div>
              </div>

              <div>
                <label htmlFor="confirm" className="mb-2 block text-sm font-medium text-white">Confirm Password</label>
                <input
                  id="confirm"
                  name="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                />
              </div>

              <div className="flex justify-center rounded-xl border border-white/10 bg-white/5 py-3">
                <ReCAPTCHA
                  sitekey="6LeEdM8rAAAAANDYuqmGgLkqw9MmSv_2TzTEai6Q"
                  ref={recaptchaRef}
                  onChange={(token) => setCaptchaToken(token)}
                  size="normal"
                />
              </div>

              {passError && <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">{passError}</div>}

              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                  loading
                    ? 'cursor-not-allowed bg-indigo-500/40'
                    : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                }`}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          )}

          {step === 3 && (
            <div className="text-center">
              <p className="text-green-300 text-sm">{successMsg}</p>
              <button
                className="mt-6 w-full rounded-xl py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500"
                onClick={() => {
                  navigate('/');
                  window.location.reload();
                }}
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasswordReset;
